import { FC } from "react";
import { AccuracyGapAnalysisPanel } from "@app/projects/accuracy-gap-analysis";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { FlyoutLink } from "@app/shared/Flyouts";
import { formatDateTime } from "@app/shared/formatDateTime";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import Roles from "@app/user/roles";
import { AccuracyGapAnalysisConfiguration } from "@app/projects/accuracy-gap-analysis/accuracyGapAnalysisConfiguration";

interface AgaAlertProps {
  compareToRunConfiguration: ProjectRunConfiguration;
  isDualMembershipMode: boolean;
  primaryConfiguration: AccuracyGapAnalysisConfiguration;
  projectId: number;
  runConfiguration: ProjectRunConfiguration;
  supplementaryConfiguration?: AccuracyGapAnalysisConfiguration;
  userRoles: string[];
}

export const AgaAlert: FC<React.PropsWithChildren<AgaAlertProps>> = (props: AgaAlertProps) => {
  if (!(
    (props.userRoles.includes(Roles.ACCURACY_LICENSE_TIER_1) || props.userRoles.includes(Roles.ACCURACY_LICENSE_TIER_2)) &&
    !!props.runConfiguration &&
    !(props.runConfiguration.isProjected || props.runConfiguration.isTermed || props.runConfiguration.isProjectedTermed)
  ) ||
    (!!props.compareToRunConfiguration && !props.isDualMembershipMode) ||
    !props.primaryConfiguration) {
    return null;
  }

  const getPluralization = (numberOfGaps: number) => {
    return numberOfGaps === 1 ? "gap" : "gaps";
  }

  const timestamp = formatDateTime(props.runConfiguration.datePublished);

  return (
    <QESAlert type="info" key="aga" classNames="mt-3">
      {props.isDualMembershipMode && props.supplementaryConfiguration && props.compareToRunConfiguration ? (
        `${props.primaryConfiguration?.projectedScore.gapsChange} ${props.runConfiguration.memberSetAlias} ${getPluralization(props.primaryConfiguration?.projectedScore.gapsChange)} and ${props.supplementaryConfiguration.projectedScore.gapsChange} ${props.compareToRunConfiguration.memberSetAlias} ${getPluralization(props.supplementaryConfiguration.projectedScore.gapsChange)} are projected in your network due to inaccurate data.`
      ) : (
        `${props.primaryConfiguration?.projectedScore.gapsChange} ${getPluralization(props.primaryConfiguration?.projectedScore.gapsChange)} are projected in your network due to inaccurate data.`
      )}
      <FlyoutLink
        classNames="ms-2"
        content={{
          body: (
            <AccuracyGapAnalysisPanel
              projectId={props.projectId}
              projectIsDualMembership={props.isDualMembershipMode}
              primaryMembership={props.runConfiguration.memberSetAlias}
              supplementaryMembership={props.compareToRunConfiguration?.memberSetAlias}
              status={props.runConfiguration.accuracyGapAnalysisStatus}
            />
          ),
          title: "Accuracy Gap Analysis for Adequacy",
          topActionItems: [<span key="timestamp" className="copy-text-default me-3 pt-1">{timestamp && `As of last refresh: ${timestamp}`}</span>],
        }}
        testId="aga-alert__flyout"
      >
        View Accuracy Gap Analysis
      </FlyoutLink>
    </QESAlert>
  );
};
